import { defineStore } from 'pinia'
import saasAPiClientCallBackend from '../saasAPiClientCallBackend.js'
import callbackHelper from '../callbackHelper'
import capacitorDetect from '../capacitorDetect'
import transmissionManager from '../transmissionManager'

export const matrixChatStoreCookieName = 'saasSocialMatrixLoginSession'

export const MUTE_DURATIONS = [
  { label: 'One Hour', duration: 1 * 60 * 60 * 1000 },
  { label: '24 Hours', duration: 24 * 60 * 60 * 1000 },
  { label: 'One Week', duration: 7 * 24 * 60 * 60 * 1000 },
  { label: 'One Month', duration: 30 * 24 * 60 * 60 * 1000 },
  { label: 'Forever', duration: null }
]
const ACCOUNT_DATA_MUTED_ROOMS = 'club.muted_rooms'

export function getChatStore () {
  return useChatStore()
}

const initStateMeanings = {
  0: 'Newly setup',
  1: 'public config recieved',
  2: 'establishing login session',
  3: 'connecting to chat server',
  4: 'login session established',
  99: 'Login failed'
}

export const constRoomTimeline = 'Room.timeline'
export const constRoomLocalEchoUpdated = 'Room.localEchoUpdated'

export function isDirectChat (room, dmMapping) {
  const isDM = Object.values(dmMapping).some(roomIds => roomIds.includes(room.roomId))
  return isDM // || isTwoPeople
}

export const useChatStore = defineStore('chat', {
  state: () => ({
    init_state: 0, // see initStateMeanings
    public_config: {},
    login_session: {},
    matrix_sdk: undefined,
    matrix: undefined,
    matrix_state: '',
    roomListeners: new Map(),
    userProfileCache: {},
    userProfilePromises: {}, // profile fetches in process
    pendingMessagesVersion: 0,
    mutedRooms: {
      // roomId: expiryTimestamp | null
    }
  }),

  getters: {
    get_init_state (state) {
      return {
        value: state.init_state,
        meaning: initStateMeanings[state.init_state]
      }
    },
    get_chat_public_config (state) {
      return state.public_config
    },
    get_chat_loggedin_user_id (state) {
      if (state.init_state !== 4) {
        return 'Not logged in'
      }
      return state.login_session.user_id
    },
    get_loginsession_debug (state) {
      // only device ID is not private
      return state.login_session.device_id
    },
    isLoggedIn (state) {
      return state.init_state === 4
    },
    rooms (state) {
      if (state.init_state !== 4) {
        return []
      }
      return state.matrix.getRooms()
    },
    get_room (state) {
      return function (roomId) {
        if (state.init_state !== 4) {
          return undefined
        }
        return state.matrix.getRoom(roomId)
      }
    },
    get_my_room_membership (state) {
      return function (roomId) {
        const room = this.get_room(roomId)
        if (typeof (room) === 'undefined') {
          return {
            name: 'undefined',
            membership: undefined
          }
        }
        if (room === null) {
          return {
            name: 'undefined',
            membership: undefined
          }
        }
        const membership = room.getMember(this.get_userId)
        if (typeof (membership) === 'undefined') {
          return {
            name: 'undefined',
            membership: undefined
          }
        }
        // can be 'invite' 'join' 'leave' 'ban' 'knock'
        return {
          name: membership.membership,
          membership
        }
      }
    },
    get_userId (state) {
      if (state.init_state !== 4) {
        return undefined
      }
      return state.matrix.getUserId()
    },
    get_isDirectChatFn (state) {
      return async function (room) {
        const dmMapping = await this.get_accountDataMDirect()
        return isDirectChat(room, dmMapping)
      }
    },
    mxcUrlToHttp (state) {
      return async function (mxcUrl) {
        return state.matrix.mxcUrlToHttp(mxcUrl)
      }
    }
  },
  actions: {
    async leaveRoom (roomId, updatemdirect) {
      try {
        await this.matrix.leave(roomId)
        // We do not do this. Leave old rooms marked as direct
        //  when new direct chats are started then we will check
        //  both members are present or invited. So once one has
        //  left that room is no longer used
        // if (updatemdirect) {
        //  await this.updateDirectMappingRemove(roomId)
        // }
      } catch (err) {
        console.error('Failed to leave room:', err)
      }
    },
    async leaveAllDmsWithUser (matrixUserId) {
      const mdirect = await this.get_accountDataMDirect() || {}
      const roomIds = mdirect[matrixUserId]
      if (!Array.isArray(roomIds) || roomIds.length === 0) {
        return
      }
      for (const roomId of roomIds) {
        await this.leaveRoom(roomId, false)
      }
      delete mdirect[matrixUserId]
      await this.set_accountDataMDirect(mdirect)
    },
    async resetmdirect () {
      await this.set_accountDataMDirect({})
    },
    async waitForPrepared (timeoutMs = 10000) {
      // Wait until this.matrix is set
      await new Promise((resolve, reject) => {
        const checkInterval = 50 // ms
        let waited = 0

        const check = () => {
          if (this.matrix) {
            resolve()
          } else {
            waited += checkInterval
            if (waited >= timeoutMs) {
              reject(new Error('Timeout waiting for this.matrix to be initialized'))
            } else {
              setTimeout(check, checkInterval)
            }
          }
        }

        check()
      })

      const state = this.matrix.getSyncState()

      // Already ready?
      if (state === 'PREPARED' || state === 'SYNCING' || state === 'RECONNECTING') {
        return
      }

      await Promise.race([
        new Promise((resolve, reject) => {
          const handler = (newState) => {
            if (
              newState === 'PREPARED' ||
              newState === 'SYNCING' ||
              newState === 'RECONNECTING'
            ) {
              this.matrix.off('sync', handler)
              resolve()
            }

            if (newState === 'ERROR') {
              this.matrix.off('sync', handler)
              reject(new Error('Matrix sync entered ERROR state'))
            }
          }

          this.matrix.on('sync', handler)
        }),
        new Promise((resolve, reject) =>
          setTimeout(() => reject(new Error('Timeout waiting for sync ready')), timeoutMs)
        )
      ])
    },
    async get_accountDataMDirect () {
      await this.waitForPrepared()

      const evt = this.matrix.getAccountData('m.direct')
      return JSON.parse(JSON.stringify(evt?.getContent() || {}))
    },
    async set_accountDataMDirect (content) {
      try {
        await this.matrix.setAccountData('m.direct', content)
      } catch (err) {
        console.error('Failed to write m.direct', err)
        return false
      }
    },
    async startDirectMessage (userId) {
      // Typical user Id
      // @socialpsocial1e458e4f-75d1-4680-bfde-1e644afe5371:socialchat.metcarob.com
      const existingRoomId = await this.findExistingDMRoom(userId)
      if (existingRoomId) {
        return existingRoomId
      }
      const response = await this.matrix.createRoom({
        invite: [userId],
        is_direct: true,
        preset: 'trusted_private_chat',
        // No encryption state!
        room_name: userId + ' - ' + this.matrix.getUserId(),
        room_topic: 'Direct Chat'

      })
      const roomId = response.room_id
      console.log('RJMDEBUG001 create dm adding roomid', response.room_id)
      this.updateDirectMappingAdd(userId, roomId)
      return roomId
    },
    async updateDirectMappingAdd (userId, roomId) {
      // Always fall back to an empty object if undefined
      const mdirect = await this.get_accountDataMDirect() || {}

      // Ensure the user entry is an array
      const existingRooms = Array.isArray(mdirect[userId]) ? mdirect[userId] : []

      // Add the room ID without duplicates
      const rooms = new Set(existingRooms)
      rooms.add(roomId)

      // Update the mapping
      mdirect[userId] = [...rooms]

      // Save updated data
      await this.set_accountDataMDirect(mdirect)
    },
    async updateDirectMappingRemove (roomId) {
      const mdirect = await this.get_accountDataMDirect() || {}
      let changed = false

      // Iterate over all userIds in the map
      for (const userId of Object.keys(mdirect)) {
        const rooms = mdirect[userId]
        if (!Array.isArray(rooms)) continue

        if (rooms.includes(roomId)) {
          // Remove the roomId
          const filtered = rooms.filter(r => r !== roomId)

          if (filtered.length === 0) {
            // Remove this user entry altogether
            delete mdirect[userId]
          } else {
            mdirect[userId] = filtered
          }
          changed = true
        }
      }
      if (changed) {
        await this.set_accountDataMDirect(mdirect)
      }
    },
    async findExistingDMRoom (userId) {
      const content = await this.get_accountDataMDirect()

      const roomIds = content[userId]
      if (!roomIds) return null

      // Validate the rooms (sometimes rooms get stale)
      for (const roomId of roomIds) {
        const room = this.matrix.getRoom(roomId)
        if (!room) continue

        // Both users must be joined or invited. This will deal with issues where
        //  otheruser left the room. e.g. in the block flow. Without this unblock
        //  will not work
        const membership = room.getMyMembership()
        if (membership === 'join' || membership === 'invite') {
          const member = room.getMember(userId)
          const theirMembership = member?.membership
          if (theirMembership === 'join' || theirMembership === 'invite') {
            return roomId
          }
        }
      }
    },
    getPendingMessageList () {
      // Do not need to incrment version - only changes to the store increment it
      return transmissionManager.getPendingMessageList()
    },
    async registerRotatedPushToken ({ tokenValue }) {
      return capacitorDetect.registerRotatedPushToken({ matrixClient: this.matrix, tokenValue })
    },
    getProfileInfo (userId, forceRefresh, callback) {
      if (forceRefresh) {
        delete this.userProfileCache[userId]
        delete this.userProfilePromises[userId]
      }
      if (userId in this.userProfileCache) {
        callback.ok(this.userProfileCache[userId])
        return this.userProfileCache[userId]
      }

      // If a request is already in-flight → attach to it
      if (this.userProfilePromises[userId]) {
        this.userProfilePromises[userId].then(profile => callback.ok(profile))
        return
      }

      const TTT = this
      const promise = this.matrix.getProfileInfo(userId)
        .then(profile => {
          if ('avatar_url' in profile) {
            profile.avatar_url = this.matrix.mxcUrlToHttp(profile.avatar_url, 96, 96, 'crop')
          }
          TTT.userProfileCache[userId] = profile
          delete TTT.userProfilePromises[userId]
          callback.ok(profile)
          return profile
        })
        .catch(err => {
          delete TTT.userProfilePromises[userId]
          throw err
        })

      this.userProfilePromises[userId] = promise
    },
    fetchRoomEvent (...args) {
      return this.matrix.fetchRoomEvent(...args)
    },
    scrollback (...args) {
      return this.matrix.scrollback(...args)
    },
    makeTxnId (...args) {
      return this.matrix.makeTxnId(...args)
    },
    sendEvent ({ txnId, processData, roomId, messageType, content, globalEventBus }) {
      // Pass event to transmission manager
      const TTT = this
      transmissionManager.sendMessage({ txnId, processData, roomId, messageType, content }).then(function () {
        TTT.pendingMessagesVersion++
        capacitorDetect.transmitMessage({ txnId, processData, roomId, messageType, content, matrix: TTT.matrix, globalEventBus })
      })
    },
    retrySendEvent ({ txnId, discard, processData, roomId, messageType, content, globalEventBus }) {
      const TTT = this
      if (discard) {
        transmissionManager.registerTransmissionDiscarded({ txnId }).then(function () {
          TTT.pendingMessagesVersion++
        })
        return
      }
      transmissionManager.registerTransmissionRetry({ txnId }).then(function () {
        TTT.pendingMessagesVersion++
        capacitorDetect.transmitMessage({ txnId, processData, roomId, messageType, content, matrix: TTT.matrix, globalEventBus })
      })
    },
    sendReadReceipt (...args) {
      return this.matrix.sendReadReceipt(...args)
    },
    async init_chat_store ({ tenantName, router, serverInfo, matrix, userDetailsStore }) {
      console.log('init_chat_store')
      if (this.init_state !== 0) {
        return // Already inited
      }
      this.public_config = serverInfo.Derived.chat_public_config
      this.init_state = 1
      this.matrix_sdk = matrix
      // this.get_credentails_and_establish_login_session({ tenantName, router })

      // May also have a matrix login cookie - need to set that as well
      if (!await capacitorDetect.cookies.has(matrixChatStoreCookieName)) {
        return // no cookie
      }
      const matrixCookieData = await capacitorDetect.cookies.get(matrixChatStoreCookieName)
      if (typeof (matrixCookieData) === 'undefined') {
        return // blanked cookie so do nothing
      }
      console.log('init_chat_store - found session cookie')
      this.establish_login_session({
        loginSession: matrixCookieData,
        callback: undefined,
        userDetailsStore
      })
    },
    get_credentails_and_establish_login_session ({ tenantName, router, callback, userDetailsStore }) {
      if (this.init_state === 4) {
        // already logged in
        console.log('get_credentails_and_establish_login_session already logged in')
        callback.ok(undefined)
        return
      }
      if (this.init_state === 3) {
        // log in in progress. Should not really see this
        console.log('get_credentails_and_establish_login_session - called but login is in progress')
        callback.ok(undefined)
        return
      }
      this.init_state = 2
      const TTT = this
      const callbackInt = {
        ok: async function (response) {
          await capacitorDetect.cookies.set(matrixChatStoreCookieName, response.data.login_session)
          TTT.establish_login_session({
            loginSession: response.data.login_session,
            callback,
            userDetailsStore
          })
        },
        error: function (response) {
          this.init_state = 99
          callback.error(response)
        }
      }
      saasAPiClientCallBackend.callApi({
        prefix: 'chat',
        router,
        path: '/mycredentials',
        method: 'get',
        postdata: undefined,
        callback: callbackInt,
        tenantName
      })
    },
    async establish_login_session ({ loginSession, callback, userDetailsStore }) {
      console.log('establish_login_session')
      const TTT = this
      TTT.login_session = loginSession
      TTT.init_state = 3

      // --- NEW: Request persistent storage so the OS doesn't evict cache ---
      if (navigator.storage && navigator.storage.persist) {
        const granted = await navigator.storage.persist()
        console.log('Persistent storage granted:', granted)
      }

      // --- NEW: Build the IndexedDB store, keyed per user so sessions don't collide ---
      const dbName = `matrix-store-${loginSession.user_id}`
      const store = new TTT.matrix_sdk.IndexedDBStore({
        indexedDB: window.indexedDB,
        localStorage: window.localStorage, // survives IDB eviction
        dbName
      })

      // Wire up degradation handling
      store.on('degraded', (err) => {
        console.warn('IndexedDB store degraded, falling back to memory:', err)
      })
      store.on('closed', () => {
        console.error('IndexedDB store closed unexpectedly')
      })

      // --- NEW: Load persisted state from disk before creating the client ---
      try {
        const cacheExists = await TTT.matrix_sdk.IndexedDBStore.exists(window.indexedDB, dbName)
        console.log('Matrix cache exists:', cacheExists)
        await store.startup()
      } catch (err) {
        console.warn('IndexedDB startup failed, continuing without cache:', err)
      }

      TTT.matrix = TTT.matrix_sdk.createClient({
        baseUrl: 'https://' + TTT.public_config.chat_server,
        accessToken: TTT.login_session.access_token,
        userId: TTT.login_session.user_id,
        store
      })

      await capacitorDetect.notifyMatrixConnectionDetails({
        hostname: TTT.public_config.chat_server,
        accessToken: TTT.login_session.access_token,
        userId: TTT.login_session.user_id
      })

      TTT.matrix.on('Room.myMembership', async function (room, membership, prevMembership) {
        if (membership === 'invite') {
          try {
            const inviteEvent = room.currentState.getStateEvents('m.room.member', TTT.matrix.getUserId())
            const isDirect = inviteEvent?.getContent()?.is_direct

            if (isDirect) {
              // const myUserId = TTT.matrix.getUserId()

              // Get existing m direct content
              const mDirect = await TTT.get_accountDataMDirect()

              // Identify the inviter (the person who sent the DM)
              const inviter = inviteEvent.getSender()

              if (userDetailsStore.isBlocked(inviter)) {
                console.log('Invited to room where inviter is blocked - not going to autojoin')
                await TTT.matrix.leave(room.roomId)
                return
              } else {
                if (!mDirect[inviter]) {
                  mDirect[inviter] = []
                }

                if (!mDirect[inviter].includes(room.roomId)) {
                  mDirect[inviter].push(room.roomId)
                }

                console.log('RJMDEBUG001 write back mdirect', mDirect)

                // Write it back
                await TTT.set_accountDataMDirect(mDirect)
                console.log('Marked room as DM with', inviter)
              }
            }

            console.log('RJMDEBUG001 about to autojoin')
            await TTT.matrix.joinRoom(room.roomId)
            console.log('Auto-joined %s %s', room.roomId, room.name)
          } catch (err) {
            console.error('Autojoin failed:', err)
          }
        }
      })

      TTT.matrix.startClient()
      // console.log('chatStore get_credentails_and_establish_login_session matrix client stateed')
      TTT.matrix.once('sync', async function (state, prevState, res) {
        // console.log('chatStore get_credentails_and_establish_login_session Matrix Sync')
        TTT.matrix_state = state
        if (TTT.matrix_state !== 'PREPARED') {
          TTT.init_state = 99
          console.log('Error - matrix client not prepared matrix_state=', TTT.matrix_state)
          if (typeof (callback) !== 'undefined') {
            callbackHelper.callbackWithSimpleError(callback, 'Error - matrix client not prepared')
          }
          return
        }
        TTT.init_state = 4
        await TTT.loadMutedRoomsFromAccountData()
        await TTT.syncMutedRoomsFromPushRules()
        TTT.startMuteExpiryWatcher()
        TTT.matrix.on('accountData', (event) => {
          if (event.getType() === 'm.push_rules') {
            TTT.syncMutedRoomsFromPushRules()
          }
        })
        TTT.matrix_state = state
        TTT.matrix.on(constRoomTimeline, TTT._processRoomEvent)
        TTT.matrix.on(constRoomLocalEchoUpdated, TTT._processRoomEchoUpdate)

        await capacitorDetect.registerForPushNotifications(TTT.matrix)
        if (typeof (callback) !== 'undefined') {
          callback.ok(undefined)
        }
      })
    },
    async logout () {
      console.log('RJMDEBUG100 chatstore logout')
      if (this._muteInterval) {
        clearInterval(this._muteInterval)
        this._muteInterval = null
      }
      this.mutedRooms = {}
      console.log('RJMDEBUG100 chatstore logout remove cookies')
      try {
        await capacitorDetect.cookies.remove(matrixChatStoreCookieName)
      } catch (err) {
        console.warn('capacitorDetect cookie remove failed:', err)
      }
      console.log('RJMDEBUG100 chatstore logout notify matrix')
      try {
        await capacitorDetect.notifyMatrixLogout(this.matrix)
      } catch (err) {
        console.warn('capacitorDetect notifyMatrixLogout failed:', err)
      }
      console.log('RJMDEBUG100 chatstore logout matrix')
      try {
        await this.matrix.logout(true)
      } catch (err) {
        console.warn('matrix.logout failed:', err)
      }
      console.log('RJMDEBUG100 chatstore matrix clearstores')
      try {
        await this.matrix.clearStores()
      } catch (err) {
        console.warn('matrix.clearStores failed:', err)
      }
      console.log('RJMDEBUG100 CALLING TRANSMISISON MANAGER LOGOUT')
      try {
        await transmissionManager.logout()
      } catch (err) {
        console.warn('transmissionManager logout failed:', err)
      }
      this.pendingMessagesVersion++
      this.init_state = 0 // Send back to 0 - resfreshallstores makes it reinit
      this.login_session = {}
      this.matrix_state = ''
      this.matrix = undefined
    },
    join_club_chat ({ tenantName, router, clubId, roomName, matrixRoomId, callback }) {
      // Can not pass in just matrix id because may need to call service to add user to chat
      if (this.init_state === 4) {
        this._join_club_chat_already_loggedin({ tenantName, router, clubId, matrixRoomId, roomName, callback })
        return
      }
      if (this.init_state !== 1) {
        console.log('Error - unable to login - chat store in invalid state', this.init_state)
        callbackHelper.callbackWithSimpleError(callback, 'Error - Join club chat unable to login')
        return
      }
      // Should log user in when userdetails are loaded
      callbackHelper.callbackWithSimpleError(callback, 'Error - not logged in to chat store')
    },
    _join_club_chat_already_loggedin ({ tenantName, router, clubId, roomName, matrixRoomId, callback }) {
      // console.log('TODO join club chat', tenantName, router, clubId, matrixRoomId)
      // console.log('matrixRoomId', matrixRoomId)
      // TODO does this room need matrixRoomId???
      const callbackInt = {
        ok: function (response) {
          callback.ok(response)
        },
        error: function (response) {
          this.init_state = 99
          console.log('Join room failed')
          callback.error(response)
        }
      }
      const joinRoomPostData = {
        club_id: clubId,
        room_name: roomName
      }
      saasAPiClientCallBackend.callApi({
        prefix: 'chat',
        router,
        path: '/clubroom',
        method: 'post',
        postdata: joinRoomPostData,
        callback: callbackInt,
        tenantName
      })
      // {'club_id': '9f6c76a7-9b86-424f-ac6f-5a86b1bb6607', 'room_name': 'invgen'}
    },
    _processRoomEchoUpdate (event, room, oldEventId, oldStatus) {
      const listeners = this.roomListeners.get(room.roomId)
      if (listeners) {
        for (const callback of listeners) {
          callback(constRoomLocalEchoUpdated, event, room, undefined, oldEventId, oldStatus)
        }
      }
    },
    _processRoomEvent (event, room, toStartOfTimeline) {
      const listeners = this.roomListeners.get(room.roomId)
      if (listeners) {
        for (const callback of listeners) {
          callback(constRoomTimeline, event, room, toStartOfTimeline, undefined, undefined)
        }
      }
    },
    registerRoomCallback (roomId, callback) {
      if (typeof callback !== 'function') {
        throw new TypeError('Callback must be a function')
      }
      if (!this.roomListeners.has(roomId)) {
        this.roomListeners.set(roomId, new Set())
      }
      this.roomListeners.get(roomId).add(callback)
    },
    unregisterEventCallback (callback) {
      for (const [eventType, listeners] of this.roomListeners.entries()) {
        if (listeners.has(callback)) {
          listeners.delete(callback)
          if (listeners.size === 0) {
            this.roomListeners.delete(eventType) // Clean up empty sets
          }
        }
      }
    },
    messageTransmissionComplete (eventData) {
      const TTT = this
      transmissionManager.registerTransmissionComplete(eventData).then(function () {
        TTT.pendingMessagesVersion++
      })
    },
    resyncNow (eventData) {
      // console.log('RJMDEBUG001 chatstore resync NOW')
      this.matrix.resync()
    },
    async muteRoom (roomId, duration) {
      let expiry = -1
      if (typeof duration === 'number' && duration !== -1) {
        expiry = Date.now() + duration
      }
      this.mutedRooms[roomId] = expiry
      try {
        await this.matrix.setRoomMutePushRule('global', roomId, true)
        await this.saveMutedRoomsToAccountData()
      } catch (err) {
        console.error('Failed to mute room', err)
        delete this.mutedRooms[roomId]
      }
    },
    async unmuteRoom (roomId) {
      delete this.mutedRooms[roomId]
      try {
        await this.matrix.setRoomMutePushRule('global', roomId, false)
        await this.saveMutedRoomsToAccountData()
      } catch (err) {
        console.error('Failed to unmute room', err)
      }
    },
    isMuted (roomId) {
      const expiry = this.mutedRooms[roomId]
      if (expiry === undefined) return false
      if (expiry === -1) return true
      return typeof expiry === 'number' && Date.now() < expiry
    },
    async saveMutedRoomsToAccountData () {
      try {
        await this.matrix.setAccountData(
          ACCOUNT_DATA_MUTED_ROOMS,
          this.mutedRooms
        )
      } catch (err) {
        console.error('Failed saving muted rooms account data', err)
      }
    },
    async loadMutedRoomsFromAccountData () {
      await this.waitForPrepared()
      const evt = this.matrix.getAccountData(ACCOUNT_DATA_MUTED_ROOMS)
      const data = evt?.getContent() || {}
      this.mutedRooms = {}
      Object.assign(this.mutedRooms, data)
    },
    async checkMuteExpiries () {
      const tasks = []
      const now = Date.now()
      for (const roomId of Object.keys(this.mutedRooms)) {
        const expiry = this.mutedRooms[roomId]
        if (expiry !== -1 && now >= expiry) {
          tasks.push(
            this.unmuteRoom(roomId).catch(err => {
              console.error('Failed to auto-unmute room', roomId, err)
            })
          )
        }
      }
      await Promise.all(tasks)
    },
    async syncMutedRoomsFromPushRules () {
      const pushRules = this.matrix.getPushRules()
      if (!pushRules?.global?.room) return
      const mutedFromServer = {}
      for (const rule of pushRules.global.room || []) {
        if (rule.actions?.includes('dont_notify')) {
          mutedFromServer[rule.rule_id] = true
        }
      }
      // Remove locally muted rooms that are no longer muted on server, but only if not forever
      for (const roomId of Object.keys(this.mutedRooms)) {
        const expiry = this.mutedRooms[roomId]
        if (expiry !== null && !(roomId in mutedFromServer)) {
          delete this.mutedRooms[roomId]
        }
      }
      // Add missing ones from server
      for (const roomId of Object.keys(mutedFromServer)) {
        if (!(roomId in this.mutedRooms)) {
          this.mutedRooms[roomId] = -1
        }
      }
      await this.checkMuteExpiries()
    },
    startMuteExpiryWatcher () {
      if (this._muteInterval) return
      this._muteInterval = setInterval(() => {
        this.checkMuteExpiries()
      }, 60000 * 5)
    }
  }
})
